import React, { ComponentType, MouseEventHandler, ReactNode, Ref } from 'react';
import styled from 'styled-components';

import { Icon, IconName } from 'components/Icon';
import { Color, colors, fontWeights } from 'theme';

import { buttonSize, buttonStylesMinimal } from './buttonStyles';

export type Size = 'small' | 'medium' | 'large';
export type Variant =
  | 'primary'
  | 'secondary'
  | 'secondary-white'
  | 'danger'
  | 'danger-outline'
  | 'tertiary'
  | 'tertiary-blue'
  | 'tertiary-white'
  | 'text';

type StyledButtonProps = {
  id?: string;
  'aria-label'?: string;
  'data-qa-id'?: string;
  'data-qa-submitting'?: boolean;
  as?: string | ComponentType;
  className?: string;
  disabled?: boolean;
  href?: string;
  onBlur?: MouseEventHandler<HTMLInputElement>;
  onClick?: MouseEventHandler<HTMLInputElement>;
  onFocus?: MouseEventHandler<HTMLInputElement>;
  ref?: Ref<HTMLInputElement>;
  role?: string;
  tabIndex?: number;
  target?: string;
  title?: string;
  to?:
    | string
    | {
        pathname: string;
        state?: Record<string, unknown>;
        search?: string;
      };
  type?: 'button' | 'submit' | 'reset';
};

type Props = StyledButtonProps & {
  children?: ReactNode;
  iconName?: IconName;
  iconRight?: boolean;
  size?: Size;
  variant?: Variant;
};

const DEFAULT_VARIANT = 'primary';

// name of colors for icon initial color
const TEXT_COLORS: Record<Variant, Color> = {
  primary: 'white',
  secondary: 'waterBlue',
  'secondary-white': 'white',
  danger: 'white',
  'danger-outline': 'brick',
  tertiary: 'greyishBrown',
  'tertiary-blue': 'waterBlue',
  'tertiary-white': 'white',
  text: 'waterBlue',
};

const StyledButton = styled.button<StyledButtonProps>`
  ${buttonStylesMinimal};
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid transparent;

  /* button variants: */
  &.primary {
    background-color: ${colors.waterBlue};
    border-color: ${colors.waterBlue};
    color: ${colors[TEXT_COLORS.primary]};
    &:hover,
    &:focus {
      background-color: ${colors.hoverBlue};
      border-color: ${colors.hoverBlue};
      text-decoration: none;
    }
  }
  &.secondary {
    border-color: ${colors.waterBlue};
    color: ${colors[TEXT_COLORS.secondary]};
    &:hover,
    &:focus {
      background-color: ${colors.softBlue};
      text-decoration: none;
    }
  }
  &.secondary-white {
    border-color: ${colors.white};
    color: ${TEXT_COLORS['secondary-white']};
    &:hover,
    &:focus {
      color: ${colors.waterBlue};
      background-color: ${colors.softBlue};
      text-decoration: none;
    }
  }
  &.danger {
    background-color: ${colors.scarlet};
    border-color: ${colors.scarlet};
    color: ${colors[TEXT_COLORS.danger]};
    &:hover,
    &:focus {
      background-color: ${colors.brick};
      border-color: ${colors.brick};
      text-decoration: none;
    }
  }
  &.danger-outline {
    border-color: ${colors.scarlet};
    color: ${colors[TEXT_COLORS['danger-outline']]};
    &:hover,
    &:focus {
      background-color: ${colors.brick};
      color: white;
      text-decoration: none;
    }
  }
  &.tertiary {
    color: ${colors[TEXT_COLORS.tertiary]};

    &:hover,
    &:focus {
      border-color: ${colors.greyishBrown};
      text-decoration: none;
    }
  }
  &.tertiary-blue {
    color: ${TEXT_COLORS['tertiary-blue']};

    &:hover,
    &:focus {
      border-color: ${colors.waterBlue};
      text-decoration: none;
    }
  }
  &.tertiary-white {
    color: ${TEXT_COLORS['tertiary-white']};

    &:hover,
    &:focus {
      border-color: ${colors.white};
      text-decoration: none;
    }
  }
  &.text {
    color: ${colors[TEXT_COLORS.text]};
    font-weight: ${fontWeights.bold};
    &:hover,
    &:focus {
      color: ${colors.hoverBlue};
      text-decoration: underline;
    }
  }
  /* button sizes: */
  &.small {
    ${buttonSize({ size: 'small' })};
  }
  &.medium {
    ${buttonSize({ size: 'medium' })};
  }
  &.large {
    ${buttonSize({ size: 'large' })};
  }
`;

const ChildrenContainer = styled.div`
  text-align: center;
  width: 100%;
`;

// icons have a negative margin to compensate for padding internal to the SVG
const IconContainer = styled.div`
  margin: -1px -2px;
`;

export function Button({
  id,
  'aria-label': ariaLabel,
  'data-qa-id': dataQaId,
  'data-qa-submitting': dataQaSubmitting,
  as,
  children,
  className,
  disabled,
  href,
  iconName,
  iconRight,
  onBlur,
  onClick,
  onFocus,
  ref,
  role,
  size,
  tabIndex,
  target,
  title,
  to,
  type,
  variant,
}: Props) {
  const activeVariant = variant || DEFAULT_VARIANT;

  return (
    <StyledButton
      id={id}
      as={as}
      className={`${className || ''} ${activeVariant} ${size || 'medium'}`}
      data-qa-id={dataQaId}
      data-qa-submitting={dataQaSubmitting}
      aria-label={ariaLabel}
      type={type === undefined ? 'button' : type}
      disabled={disabled}
      onClick={onClick}
      onFocus={onFocus}
      onBlur={onBlur}
      tabIndex={tabIndex}
      href={href}
      ref={ref}
      role={role}
      target={target}
      title={title}
      to={to}
    >
      {iconName && !iconRight && (
        <IconContainer>
          <Icon
            name={iconName}
            size={20}
            initialColorName={TEXT_COLORS[activeVariant]}
          />
        </IconContainer>
      )}
      {children && <ChildrenContainer>{children}</ChildrenContainer>}
      {iconName && iconRight && (
        <IconContainer>
          <Icon
            name={iconName}
            size={20}
            initialColorName={TEXT_COLORS[activeVariant]}
          />
        </IconContainer>
      )}
    </StyledButton>
  );
}
